import {
  AETHER_APEX,
  AVALANCHE,
  BATTERING_CHARGE,
  BULWARK_BELL,
  DAWNBREAK,
  LIGHTSTEP,
  NIGHTFALL,
  PHANTOM_VAULT,
  PHASE_LUNGE,
  SEISMIC_SLAM,
  SHADOWBLINK,
  VELOCITY_GATE,
} from './abilities';
import { GRACE, MOMENTUM, SHADOW, TEMPO } from './passives';
import type { CharacterDef } from './types';

/**
 * Hero definitions. Movement feel is a sparse `tuning` delta over TUNING_DEFAULTS
 * (base values in src/movement/constants.ts: airAccelerate 150, groundAccelerate
 * 5.5, friction 4, stopSpeed 75, maxSpeed 260, airCap 30, jumpVelocity 268.3).
 *
 * All heroes are active, each with a distinct three-skill kit (see
 * src/characters/abilities.ts). The Surfer keeps an empty kit on purpose — the
 * pure, ability-free baseline. Single names throughout.
 */

/** The neutral default — an empty delta, so it surfs exactly like the base game. */
const SURFER: CharacterDef = {
  id: 'surfer',
  name: 'The Surfer',
  className: 'Default · no class',
  blurb: 'The pure, unmodified surf feel. The baseline every hero is measured against.',
  weakness: 'No identity — no strengths, no trade-offs.',
  accent: '#9fb0c0',
  portrait: 'surfer',
  tuning: {}, // identical to TUNING_DEFAULTS
};

/** Vaelith — razor air control, with a gate + flight kit. */
const VAELITH: CharacterDef = {
  id: 'vaelith',
  name: 'Vaelith',
  className: 'Elven Arcane Skyrunner',
  blurb: 'Razor-sharp air control that rewards perfect strafe sync — and punishes sloppy input.',
  weakness: 'Unforgiving on the ground; scrubs speed fast when you stop carving.',
  accent: '#6aa6ff',
  portrait: 'vaelith',
  // Technical: sharper, more responsive air strafing (higher air accel + cap)
  // bought with grippier, less-forgiving ground friction.
  tuning: { airAccelerate: 172, airCap: 34, friction: 4.6 },
  abilities: [PHASE_LUNGE, VELOCITY_GATE, AETHER_APEX],
  passive: TEMPO,
};

const SERAPHINE: CharacterDef = {
  id: 'seraphine',
  name: 'Seraphine',
  className: 'Human Paladin',
  blurb: 'Steady and forgiving — mistakes bleed less speed, so it is the kindest line to learn on.',
  weakness: 'Lower top-end ground speed; not the line for chasing records.',
  accent: '#ffd45e',
  portrait: 'seraphine',
  tuning: { friction: 3.2, stopSpeed: 60, airAccelerate: 140, maxSpeed: 250 },
  abilities: [LIGHTSTEP, BULWARK_BELL, DAWNBREAK],
  passive: GRACE,
};

const BRAKKA: CharacterDef = {
  id: 'brakka',
  name: 'Brakka',
  className: 'Dwarven Barbarian',
  blurb: 'Heavy and unstoppable — once rolling, he sheds very little speed on the ground.',
  weakness: 'Sluggish in the air; last-second strafe corrections come slowly.',
  accent: '#ff8a3d',
  portrait: 'brakka',
  tuning: { friction: 2.6, stopSpeed: 55, airAccelerate: 120, airCap: 26 },
  abilities: [SEISMIC_SLAM, BATTERING_CHARGE, AVALANCHE],
  passive: MOMENTUM,
};

const NYX: CharacterDef = {
  id: 'nyx',
  name: 'Nyx',
  className: 'Tiefling Rogue',
  blurb: 'Nimble and springy — higher hops and quick air turns for greedy, edge-hugging lines.',
  weakness: 'Recovers poorly out in the open, away from ramps and edges.',
  accent: '#b884ff',
  portrait: 'nyx',
  tuning: { jumpVelocity: 280, airAccelerate: 160 },
  abilities: [SHADOWBLINK, PHANTOM_VAULT, NIGHTFALL],
  passive: SHADOW,
};

/** Every defined hero (kept for tests + future re-activation). */
export const ALL_CHARACTERS: readonly CharacterDef[] = [SURFER, VAELITH, SERAPHINE, BRAKKA, NYX];

/** The heroes currently selectable in the game. */
export const CHARACTERS: readonly CharacterDef[] = [SURFER, VAELITH, SERAPHINE, BRAKKA, NYX];

/** The default selection when a player has never chosen one (or picked an unknown id). */
export const DEFAULT_CHARACTER = VAELITH;
export const DEFAULT_CHARACTER_ID = VAELITH.id;

/** Look up an active hero by id, falling back to the default for unknown ids. */
export function resolveCharacter(id: string | null | undefined): CharacterDef {
  return CHARACTERS.find((c) => c.id === id) ?? DEFAULT_CHARACTER;
}
